<mat-card class="provider-status-card">
    <div class="provider-status-card__header">
        <div>
            <h2>Payroll Provider Connections</h2>
            <p>Super Admin summary for configured payroll provider connections.</p>
        </div>
        <button mat-stroked-button type="button" [disabled]="isRefreshingProviderConnectionStatus" (click)="refreshProviderConnectionStatusSummary()">
            <mat-icon>refresh</mat-icon>
            Refresh
        </button>
    </div>

    <p class="error-text" *ngIf="providerConnectionStatusError">{{ providerConnectionStatusError }}</p>

    <ng-container *ngIf="providerConnectionStatusSummary as providerSummary">
        <div class="provider-status-grid">
            <div class="summary-item">
                <span>Configured Providers</span>
                <strong>{{ providerSummary.totals.configuredProviders || 0 }}</strong>
            </div>
            <div class="summary-item">
                <span>Active Connections</span>
                <strong>{{ providerSummary.totals.activeProviderConnections || 0 }}</strong>
            </div>
            <div class="summary-item">
                <span>Companies With Credentials</span>
                <strong>{{ providerSummary.totals.companiesWithActiveProviderCredentials || 0 }}</strong>
            </div>
            <div class="summary-item">
                <span>Credential Records</span>
                <strong>{{ providerSummary.totals.providerCredentialRecords || 0 }}</strong>
            </div>
        </div>

        <p class="helper-text" *ngIf="providerSummary.providers.length === 0">
            No payroll provider connections are currently configured.
        </p>

        <div class="provider-status-list" *ngIf="providerSummary.providers.length > 0">
            <div
                    class="provider-status-row"
                    *ngFor="let provider of providerSummary.providers; trackBy: trackByProviderConnectionStatus">
                <div class="provider-status-row__title">
                    <div>
                        <strong>{{ provider.name }}</strong>
                        <span>{{ provider.providerSlug }}</span>
                    </div>
                    <span class="provider-status-pill" [ngClass]="getProviderStatusPillClass(provider.status)">
                        {{ getProviderStatusLabel(provider.status) }}
                    </span>
                </div>
                <div class="provider-status-row__metrics">
                    <span>{{ provider.activeConnections || 0 }}/{{ provider.totalConnections || 0 }} active</span>
                    <span>{{ provider.activeCredentialCompanies || 0 }} with credentials</span>
                    <span>{{ provider.availableSettings || 0 }} available settings</span>
                    <span>Statuses: {{ getConnectionStatusSummary(provider) }}</span>
                    <span>Last verified: {{ provider.latestVerifiedAt ? (provider.latestVerifiedAt | date:'short') : '---' }}</span>
                </div>
            </div>
        </div>
    </ng-container>
</mat-card>

<mat-card class="ukg-status-card">
    <div class="ukg-status-card__header">
        <div>
            <h2>UKG Sandbox Status</h2>
            <p>Super Admin summary for the shared UKG Ready sandbox environment.</p>
        </div>
        <div class="ukg-status-card__actions">
            <button mat-stroked-button type="button" [disabled]="isRefreshingUkgStatus" (click)="refreshUkgStatusSummary()">
                <mat-icon>refresh</mat-icon>
                Refresh
            </button>
            <button mat-raised-button color="primary" type="button" [disabled]="isRefreshingUkgStatus" (click)="recheckUkgStatus('impacted')">
                Re-check Impacted
            </button>
            <button mat-stroked-button type="button" [disabled]="isRefreshingUkgStatus" (click)="recheckUkgStatus('all')">
                Re-check All
            </button>
        </div>
    </div>

    <p class="error-text" *ngIf="ukgStatusError">{{ ukgStatusError }}</p>
    <p class="helper-text" *ngIf="ukgStatusRefreshMessage">
        {{ ukgStatusRefreshMessage }}<span *ngIf="ukgStatusRefreshAt"> Updated {{ ukgStatusRefreshAt | date:'shortTime' }}.</span>
    </p>

    <ng-container *ngIf="ukgStatusSummary as summary">
        <div class="ukg-status-card__summary">
            <span class="ukg-status-pill" [ngClass]="getUkgStatusPillClass(summary.overallState)">
                {{ getUkgStatusLabel(summary.overallState) }}
            </span>
            <span class="ukg-status-card__env">
                {{ summary.environmentLabel | titlecase }}<span *ngIf="summary.host"> • {{ summary.host }}</span>
            </span>
        </div>

        <div class="ukg-status-grid">
            <div class="summary-item">
                <span>Impacted Companies</span>
                <strong>{{ summary.impactedCompanyCount || 0 }}/{{ summary.totalConfiguredCompanies || 0 }}</strong>
            </div>
            <div class="summary-item">
                <span>Outage Started</span>
                <strong>{{ summary.currentIncidentStartedAt ? (summary.currentIncidentStartedAt | date:'short') : '---' }}</strong>
            </div>
            <div class="summary-item">
                <span>Outage Age</span>
                <strong>{{ getUkgOutageAge(summary) }}</strong>
            </div>
            <div class="summary-item">
                <span>Last Success</span>
                <strong>{{ summary.lastSuccessAt ? (summary.lastSuccessAt | date:'short') : '---' }}</strong>
            </div>
            <div class="summary-item">
                <span>Last Failed Check</span>
                <strong>{{ summary.lastFailureAt ? (summary.lastFailureAt | date:'short') : '---' }}</strong>
            </div>
            <div class="summary-item">
                <span>Vendor Escalation</span>
                <strong>{{ summary.shouldContactVendor ? 'Contact UKG' : 'Monitor' }}</strong>
            </div>
        </div>

        <div class="ukg-status-escalation" *ngIf="summary.shouldContactVendor">
            UKG sandbox downtime has exceeded {{ summary.escalationThresholdHours }} hours. Reach out to UKG if the outage is still blocking validation.
        </div>
    </ng-container>
</mat-card>

<mat-card class="ukg-cleanup-card" *ngIf="isUkgCleanupVisible">
    <div class="ukg-cleanup-header">
        <h2>UKG Data Cleanup</h2>
        <p>
            Run scoped cleanup for UKG-imported data with a dry-run preview before executing destructive changes.
        </p>
        <p class="cleanup-best-practice">
            Recommended full reset: run <strong>Companies</strong> scope, preview with dry run first, then execute.
            If needed, enable <strong>Delete orphan user accounts</strong> in that same run.
        </p>
    </div>

    <ng-container *ngIf="!isUkgCleanupDisabledInProduction; else ukgCleanupDisabledInProduction">
    <div class="ukg-cleanup-grid">
        <div class="cleanup-block">
            <h3>Cleanup Scope</h3>
            <mat-radio-group class="cleanup-level-group" [(ngModel)]="cleanupOptions.level">
                <mat-radio-button
                        class="cleanup-level-option"
                        *ngFor="let option of cleanupLevelOptions"
                        [value]="option.value">
                    <div class="cleanup-level-copy">
                        <span class="cleanup-level-title">{{ option.label }}</span>
                        <span class="cleanup-level-description">{{ option.description }}</span>
                    </div>
                </mat-radio-button>
            </mat-radio-group>
        </div>

        <div class="cleanup-block">
            <h3>Target Companies</h3>
            <mat-checkbox [(ngModel)]="limitToSelectedCompanies">
                Limit cleanup to selected companies
            </mat-checkbox>

            <mat-form-field class="cleanup-field" appearance="outline" *ngIf="limitToSelectedCompanies">
                <mat-label>UKG Companies</mat-label>
                <mat-select multiple [(ngModel)]="selectedCleanupCompanyIds">
                    <mat-option *ngFor="let company of ukgCompanies" [value]="company.id">
                        {{ company.name }} ({{ company.id }})
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <p class="helper-text" *ngIf="limitToSelectedCompanies && ukgCompanies.length === 0">
                No UKG companies are currently available from the API.
            </p>

            <mat-form-field class="cleanup-field" appearance="outline">
                <mat-label>Additional Company IDs (optional)</mat-label>
                <textarea
                        matInput
                        rows="3"
                        placeholder="Paste company IDs separated by commas or new lines"
                        [(ngModel)]="manualCleanupCompanyIds"></textarea>
            </mat-form-field>
        </div>

        <div class="cleanup-block">
            <h3>Safety Options</h3>
            <mat-checkbox [(ngModel)]="cleanupOptions.dryRun">
                Dry run only (no records deleted)
            </mat-checkbox>
            <mat-checkbox [(ngModel)]="cleanupOptions.deleteUserAccounts">
                Delete orphan user accounts after cleanup
            </mat-checkbox>
            <mat-checkbox
                    [(ngModel)]="cleanupOptions.forceDeleteUsers"
                    [disabled]="!cleanupOptions.deleteUserAccounts">
                Force delete users (attempt cleanup of remaining memberships/roles)
            </mat-checkbox>
            <mat-checkbox
                    [(ngModel)]="cleanupOptions.onlyPendingInviteUsers"
                    [disabled]="!cleanupOptions.deleteUserAccounts">
                Only delete pending-invite users
            </mat-checkbox>
            <mat-checkbox [(ngModel)]="cleanupOptions.includeNonUkgCompanies">
                Include non-UKG companies when explicit company IDs are provided
            </mat-checkbox>

            <mat-form-field class="cleanup-field" appearance="outline">
                <mat-label>Excluded User Emails (optional)</mat-label>
                <textarea
                        matInput
                        rows="3"
                        [disabled]="!cleanupOptions.deleteUserAccounts"
                        placeholder="Paste emails separated by commas or new lines"
                        [(ngModel)]="excludedUserEmailsInput"></textarea>
            </mat-form-field>

            <p class="helper-text warning" *ngIf="cleanupOptions.deleteUserAccounts">
                User deletion only happens when backend orphan and payroll-import checks pass.
            </p>
            <p class="helper-text" *ngIf="!cleanupOptions.deleteUserAccounts">
                Excluded user emails are only applied when "Delete orphan user accounts" is enabled.
            </p>
            <p class="helper-text warning" *ngIf="cleanupOptions.deleteUserAccounts && cleanupOptions.forceDeleteUsers">
                Force delete will try to remove remaining team memberships and company roles before deleting each user.
                Review a dry run first.
            </p>
        </div>
    </div>

    <div class="cleanup-actions">
        <button
                mat-raised-button
                color="primary"
                [disabled]="isRunningCleanup"
                (click)="runCleanup()">
            {{ getCleanupPrimaryActionLabel() }}
        </button>
        <button
                mat-stroked-button
                *ngIf="!cleanupOptions.dryRun"
                [disabled]="isRunningCleanup"
                (click)="runCleanup(true)">
            Run Dry Run Preview
        </button>
        <button
                mat-button
                [disabled]="isRunningCleanup"
                (click)="resetCleanupForm()">
            Reset Options
        </button>
    </div>

    <div class="cleanup-result" *ngIf="cleanupResult || cleanupError">
        <div class="cleanup-result-header">
            <h3>Last Cleanup Result</h3>
            <span class="helper-text" *ngIf="cleanupRunAt">{{ cleanupRunAt | date:'short' }}</span>
        </div>

        <p class="error-text" *ngIf="cleanupError">{{ cleanupError }}</p>

        <ng-container *ngIf="cleanupResult as result">
            <div class="cleanup-summary-grid">
                <div class="summary-item">
                    <span>Companies Processed</span>
                    <strong>{{ result.companiesProcessed || 0 }}</strong>
                </div>
                <div class="summary-item">
                    <span>Teams Deleted</span>
                    <strong>{{ result.totals?.teamsDeleted || 0 }}</strong>
                </div>
                <div class="summary-item">
                    <span>Memberships Removed</span>
                    <strong>{{ result.totals?.teamMembershipsRemoved || 0 }}</strong>
                </div>
                <div class="summary-item">
                    <span>Companies Deleted</span>
                    <strong>{{ result.totals?.companiesDeleted || 0 }}</strong>
                </div>
                <div class="summary-item">
                    <span>Users Deleted</span>
                    <strong>{{ result.totals?.usersDeleted || 0 }}</strong>
                </div>
                <div class="summary-item">
                    <span>User Deletes Skipped</span>
                    <strong>{{ result.totals?.usersSkipped || 0 }}</strong>
                </div>
                <div class="summary-item">
                    <span>Forced User Deletes</span>
                    <strong>{{ result.totals?.usersForceDeleted || 0 }}</strong>
                </div>
            </div>

            <p class="helper-text" *ngIf="result.missingCompanyIds?.length > 0">
                Missing company IDs: {{ result.missingCompanyIds.join(', ') }}
            </p>

            <div class="cleanup-visibility-toggle" *ngIf="result.companies?.length > 0">
                <mat-checkbox [(ngModel)]="hideNoActionCompanies">
                    Hide companies with no action
                </mat-checkbox>
            </div>

            <mat-expansion-panel class="cleanup-details-panel" *ngIf="result.companies?.length > 0">
                <mat-expansion-panel-header>
                    <mat-panel-title>
                        Company Details ({{ getVisibleCompanyDetails(result).length }})
                    </mat-panel-title>
                </mat-expansion-panel-header>

                <p class="helper-text" *ngIf="getVisibleCompanyDetails(result).length === 0">
                    No companies with actionable cleanup results.
                </p>

                <div class="cleanup-company-row" *ngFor="let company of getVisibleCompanyDetails(result); trackBy: trackByCleanupCompany">
                    <div class="cleanup-company-header">
                        <strong>{{ company.companyName || company.companyId }}</strong>
                        <span class="helper-text">{{ company.companyId }}</span>
                    </div>
                    <div class="cleanup-company-metrics">
                        <span>Teams matched: {{ company.teamsMatched?.length || 0 }}</span>
                        <span>Teams deleted: {{ company.teamsDeleted || 0 }}</span>
                        <span>Memberships removed: {{ company.teamMembershipsRemoved || 0 }}</span>
                        <span>Company deleted: {{ company.companyDeleted ? 'Yes' : 'No' }}</span>
                    </div>

                    <div class="cleanup-team-preview" *ngIf="(company.teamCleanupDetails?.length || 0) > 0">
                        <strong>Teams Targeted</strong>
                        <div class="cleanup-team-preview-list">
                            <div class="cleanup-team-preview-row" *ngFor="let teamDetail of company.teamCleanupDetails; trackBy: trackByCleanupTeamDetail">
                                <div class="cleanup-team-preview-header">
                                    <span>{{ teamDetail.teamName || 'Unnamed Team' }} ({{ teamDetail.teamId }})</span>
                                    <span class="cleanup-team-status" [ngClass]="{
                                        'cleanup-team-status--would-delete': teamDetail.action === 'would_delete',
                                        'cleanup-team-status--deleted': teamDetail.action === 'deleted',
                                        'cleanup-team-status--failed': teamDetail.action === 'failed',
                                        'cleanup-team-status--missing': teamDetail.action === 'missing_already',
                                        'cleanup-team-status--memberships': teamDetail.action === 'memberships_only'
                                    }">
                                        {{ getCleanupTeamActionLabel(teamDetail) }}
                                    </span>
                                </div>
                                <p class="helper-text" *ngIf="(teamDetail.paymentMethodsFound || 0) > 0">
                                    Payment methods tied to team: {{ teamDetail.paymentMethodsFound }}
                                </p>
                                <p class="error-text" *ngIf="teamDetail.errors?.length > 0">
                                    Team cleanup errors: {{ teamDetail.errors.join(' | ') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <p class="error-text" *ngIf="company.errors?.length > 0">
                        {{ company.errors.length }} error(s): {{ company.errors.join(' | ') }}
                    </p>
                </div>
            </mat-expansion-panel>

            <mat-expansion-panel class="cleanup-details-panel" *ngIf="hasUserCleanupDetails(result)">
                <mat-expansion-panel-header>
                    <mat-panel-title>User Cleanup Details</mat-panel-title>
                </mat-expansion-panel-header>

                <div class="cleanup-user-company" *ngFor="let company of getVisibleUserCleanupCompanies(result); trackBy: trackByCleanupCompany">
                    <h4>{{ company.companyName || company.companyId }} ({{ company.companyId }})</h4>

                    <p class="helper-text" *ngIf="(company.userCleanup?.details || []).length === 0">
                        No user cleanup candidates found for this company.
                    </p>

                    <div class="cleanup-user-row" *ngFor="let detail of company.userCleanup?.details || []; trackBy: trackByCleanupUser">
                        <div class="cleanup-user-topline">
                            <div>
                                <strong>{{ detail.userId }}</strong>
                                <span class="helper-text" *ngIf="detail.userEmail"> · {{ detail.userEmail }}</span>
                            </div>
                            <span class="cleanup-user-status" [ngClass]="{
                                'cleanup-user-status--would-delete': detail.action === 'would_delete',
                                'cleanup-user-status--deleted': detail.action === 'deleted',
                                'cleanup-user-status--failed': detail.action === 'failed',
                                'cleanup-user-status--skipped': detail.action === 'skipped'
                            }">
                                {{ getCleanupUserActionLabel(detail) }}
                                <ng-container *ngIf="detail.forced"> (forced)</ng-container>
                            </span>
                        </div>

                        <p class="helper-text">Reason: {{ detail.reason }}</p>

                        <p class="helper-text" *ngIf="detail.blockers?.length > 0">
                            Blockers: {{ detail.blockers.join(', ') }}
                        </p>
                        <p class="helper-text" *ngIf="detail.preCleanupBlockers?.length > 0">
                            Pre-cleanup blockers: {{ detail.preCleanupBlockers.join(', ') }}
                        </p>
                        <p class="helper-text" *ngIf="detail.resolvedByCurrentCleanup?.length > 0">
                            Resolved by current cleanup: {{ detail.resolvedByCurrentCleanup.join(', ') }}
                        </p>

                        <div class="cleanup-user-metrics">
                            <span>Team memberships removed: {{ detail.removedTeamMemberships || 0 }}</span>
                            <span>Company roles removed: {{ detail.removedCompanyRoles || 0 }}</span>
                            <span>
                                Vendor products removed: {{ detail.userVendorProductsDeleted || 0 }}
                                <ng-container *ngIf="(detail.userVendorProductsFound || 0) > 0">
                                    (found: {{ detail.userVendorProductsFound }})
                                </ng-container>
                            </span>
                        </div>

                        <div class="cleanup-user-lists" *ngIf="(getInitialTeamMemberships(detail).length || 0) > 0 || (getInitialCompanyRoles(detail).length || 0) > 0 || (detail.vendorProductsTargeted?.length || 0) > 0">
                            <div class="cleanup-user-list-block" *ngIf="(getInitialTeamMemberships(detail).length || 0) > 0">
                                <strong>Team Memberships Found</strong>
                                <ul>
                                    <li *ngFor="let membership of getInitialTeamMemberships(detail)">
                                        {{ formatCleanupTeamMembership(membership) }}
                                    </li>
                                </ul>
                            </div>
                            <div class="cleanup-user-list-block" *ngIf="(getInitialCompanyRoles(detail).length || 0) > 0">
                                <strong>Company Roles Found</strong>
                                <ul>
                                    <li *ngFor="let companyRole of getInitialCompanyRoles(detail)">
                                        {{ formatCleanupCompanyRole(companyRole) }}
                                    </li>
                                </ul>
                            </div>
                            <div class="cleanup-user-list-block" *ngIf="(detail.vendorProductsTargeted?.length || 0) > 0">
                                <strong>Vendor Products Targeted</strong>
                                <ul>
                                    <li *ngFor="let vendorProduct of detail.vendorProductsTargeted">
                                        {{ formatCleanupVendorProduct(vendorProduct) }}
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <p class="error-text" *ngIf="detail.forceDeleteErrors?.length > 0">
                            Force-delete cleanup errors: {{ detail.forceDeleteErrors.join(' | ') }}
                        </p>
                        <p class="error-text" *ngIf="detail.userVendorProductCleanupErrors?.length > 0">
                            Vendor product cleanup errors: {{ detail.userVendorProductCleanupErrors.join(' | ') }}
                        </p>
                    </div>
                </div>
            </mat-expansion-panel>

            <mat-expansion-panel class="cleanup-details-panel">
                <mat-expansion-panel-header>
                    <mat-panel-title>Raw Response</mat-panel-title>
                </mat-expansion-panel-header>
                <pre class="cleanup-json">{{ result | json }}</pre>
            </mat-expansion-panel>
        </ng-container>
    </div>
    </ng-container>

    <ng-template #ukgCleanupDisabledInProduction>
        <p class="helper-text warning ukg-cleanup-disabled-message">
            UKG data cleanup is disabled in production.
        </p>
    </ng-template>
</mat-card>
